import { Event } from "@prisma/client";
import { DateTime, IANAZone } from "luxon";
import { RIVER_EMAILS } from "./constants";

// Returns strings like 1m ago, 6h ago, Dec 24, 2021 etc
export function timeMap(time: number) {
    const seconds = (Date.now() - time) / 1000;
    if (seconds < 5) return "Now";
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    if (seconds < 60 * 60) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 3600 * 24) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d`;
    if (seconds < 604800 * 5) return `${Math.floor(seconds / 604800)}w`;

    // ECMA-262 Date.toString() format: Wed Dec 31 1969 19:00:00 GMT-0500 (Eastern Standard Time)
    const timeFormat = new Date(time).toString().split(" ");
    if (seconds < 2592000 * 12) return `${timeFormat[1]} ${timeFormat[2]}`;
    return `${timeFormat[1]} ${timeFormat[2]} ${timeFormat[3]}`;
}

/**
 * Returns string like MONDAY, JULY 17, 2023 AT 07:05 PM CST
 */
export const getFormattedTimeStamp = (
    time: Date,
    options: {
        isTimeSet?: boolean;
        showYear?: boolean;
        showWeekday?: boolean;
        short?: boolean;
    } = {},
) => {
    const { isTimeSet = false, showYear = true, showWeekday = true } = options;
    const inputDate = new Date(time);

    const diffDays =
        -1 *
        Math.round(
            DateTime.now()
                .startOf("day")
                .diff(adjustTzToLocal(time).startOf("day"), "days").days,
        );

    const formatted = new Intl.DateTimeFormat(undefined, {
        timeZone: "UTC",
        weekday: showWeekday ? "long" : options?.short ? "short" : undefined,
        month: options.short ? "short" : "long",
        day: "numeric",
        year: showYear ? "numeric" : undefined,
        hour: isTimeSet ? "2-digit" : undefined,
        minute: isTimeSet ? "2-digit" : undefined,
    }).format(inputDate);

    if (diffDays === 0 && showWeekday) return "Today, " + formatted;
    else if (diffDays === 1) return "Tomorrow, " + formatted;
    else if (diffDays === -1) return "Yesterday, " + formatted;

    return formatted;
};

/**
 *
 * @param time
 * @returns string like MONDAY JULY 17, 2023
 */
export const getDesginedDate = (time: Date) => {
    return Intl.DateTimeFormat(undefined, {
        timeZone: "UTC",
        weekday: "long",
        month: "long",
        day: "numeric",
    })
        .format(time)
        .replaceAll(" ", "$")
        .replaceAll(",", "$")
        .replace(/\$+/g, "$")
        .split("$")
        .join(" ");
};

/**
 *
 * @param time
 * @returns string like 5:04 PM or 4:24 PM - 4:24 PM
 */
export const getDesginedTimePeriod = (
    startTimeDate: Date,
    endTimeDate?: Date | null,
) => {
    const startTime = Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        timeStyle: "short",
    }).format(startTimeDate);

    if (!endTimeDate) {
        return startTime;
    }

    const endTime = Intl.DateTimeFormat("en-US", {
        timeStyle: "short",
        timeZone: "UTC",
    }).format(endTimeDate);

    return `${startTime} - ${endTime}`;
};

// Returns strings like Today or Yesterday or 10/22/2021
export const getFormatedTimeStampWithNoTime = (time: Date) => {
    const timeDate = new Date(time);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (timeDate.toDateString() === today.toDateString()) {
        return "Today";
    } else if (timeDate.toDateString() === yesterday.toDateString())
        return "Yesterday";

    return timeDate.toLocaleDateString();
};

// returns {date: "14 Feb 2024", time: "12:33"}
export const getFormatedTimeObject = (time: Date) => {
    const timeStr = Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        timeStyle: "short",
    }).format(time);

    const dateStr = time.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });

    return { date: dateStr, time: timeStr };
};

// returns strings like 'May 2022'
export const getMonthYear = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString([], {
        year: "numeric",
        month: "long",
    });
};

export const getHoursMinutesFromTimeStamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });
};

//gets the time difference between two timestamps in d:h:m:s format
export const timeUntil = (timestamp: number, format: "short" | "full") => {
    const now = Date.now();
    let s = Math.floor((timestamp - now) / 1000);

    //calculate days
    const d = Math.floor(s / (3600 * 24));
    s -= d * 3600 * 24;

    //calculate hours
    const h = Math.floor(s / 3600);
    s -= h * 3600;

    //calculate minutes
    const m = Math.floor(s / 60);
    s -= m * 60;

    if (format === "short") {
        // return just the value + highest category (e.g. 12d or 3h or 2m or 1s)
        if (d > 0) {
            return `${d}d`;
        } else if (h > 0) {
            return `${h}h`;
        } else if (m > 0) {
            return `${m}m`;
        } else {
            return `${s}s`;
        }
    } else {
        // return the full string (e.g. 12d 3h 2m 1s)
        const tmp = [];
        d && tmp.push(d + "d");
        (d || h) && tmp.push(h + "h");
        (d || h || m) && tmp.push(m + "m");
        tmp.push(s + "s");
        return tmp.join(" ");
    }
};

export const getDayOfWeek = (date: Date) => {
    const dayOfWeek = new Date(date).getDay();
    return isNaN(dayOfWeek)
        ? null
        : [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
          ][dayOfWeek];
};

/**
 * Get dropdown formatted date
 * @param date Date
 * @returns string like MON, 9/31
 */
export const getDropdownFormattedDate = (date: Date): string => {
    return Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        weekday: "short",
        month: "numeric",
        day: "numeric",
    }).format(
        DateTime.fromJSDate(date, { zone: "UTC" }).startOf("day").toJSDate(),
    );
};

export const adjustedTimeZoneToClient = (
    time: Date,
    originalTimezone: string,
) => {
    const retrievedTime = DateTime.fromJSDate(time, { zone: "UTC" });
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const eventZone = IANAZone.create(originalTimezone);
    const userZone = IANAZone.create(userTimezone);

    const eventOffset = eventZone.offset(retrievedTime.toMillis());
    const userOffset = userZone.offset(retrievedTime.toMillis());

    const timeDifference = userOffset - eventOffset;

    return retrievedTime.plus({ minutes: timeDifference });
};

export const adjustedTimeZoneToUTC = (time: Date, originalTimezone: string) => {
    return toSameTimeDiffTZ(time, originalTimezone, "UTC");
};

export const adjustTzToLocal = (time: Date) => {
    return DateTime.fromJSDate(time, {
        zone: "UTC",
    }).setZone("local", { keepLocalTime: true });
};

export const getCalendarLinks = (
    event:
        | Event
        | {
              name: string;
              slug: string;
              startTime: Date;
              endTime?: Date | null;
              timezone: string;
              description?: string | null;
              location: string;
          },
    communityName?: string,
) => {
    const timeFormat = "yyyyMMdd'T'HHmmss'Z'";
    const startTimeAdjustedToUTC = adjustedTimeZoneToUTC(
        event.startTime,
        event.timezone,
    );
    const formattedStartTime = startTimeAdjustedToUTC.toFormat(timeFormat);

    // default to +3h if there's no end time set...
    // (google cal does not accept events with no end time)
    const endTime =
        event.endTime ||
        DateTime.fromJSDate(event.startTime, {
            zone: "utc",
        })
            .plus({ hours: 3 })
            .toJSDate();
    const endTimeAdjustedToUTC = adjustedTimeZoneToUTC(endTime, event.timezone);
    const formattedEndTime = endTimeAdjustedToUTC.toFormat(timeFormat);

    const eventLink = `https://app.getriver.io/events/${event.slug}`;

    const encodedName = encodeURIComponent(event.name);
    const eventDescription =
        `${event.description}\n\nRiver event details: <a href="${eventLink}">${eventLink}</a>` ||
        "";

    const encodedDescription = encodeURIComponent(eventDescription);
    const encodedLocation = encodeURIComponent(event.location);

    const organizerName = communityName || "River";
    const organizerEmail = RIVER_EMAILS.INVITATIONS.email;
    const encodedOrganizerName = encodeURIComponent(organizerName);
    const encodedOrganizerEmail = encodeURIComponent(organizerEmail);

    const icsData = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "BEGIN:VEVENT",
        `URL:${eventLink}`,
        `DTSTART:${formattedStartTime}`,
        `DTEND:${formattedEndTime}`,
        `SUMMARY:${event.name}`,
        `DESCRIPTION:${(event.description || "").replaceAll("\n", "\\n")}`,
        `LOCATION:${event.location}`,
        `ORGANIZER;CN=${organizerName}:mailto:${organizerEmail}`,
        "END:VEVENT",
        "END:VCALENDAR",
    ].join("\n");

    return {
        google: `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodedName}&dates=${formattedStartTime}/${formattedEndTime}&details=${encodedDescription}&location=${encodedLocation}&sf=true&output=xml&sprop=website:${eventLink}&trp=true`,
        outlook: `https://outlook.live.com/owa/?path=/calendar/action/compose&rru=addevent&startdt=${formattedStartTime}&enddt=${formattedEndTime}&subject=${encodedName}&location=${encodedLocation}&body=${encodedDescription}&allday=false&organizer=${encodedOrganizerName}&organizer_email=${encodedOrganizerEmail}`,
        apple: `data:text/calendar;charset=utf8,${encodeURIComponent(icsData)}`,
    };
};

/**
 * Takes input timeA and converts it to a different timezone
 * which results in local geographical time that happens at same time as timeA (simulcast)
 * @param timeA Date
 * @param tzA Date
 * @param tzB Date
 * @returns DateTime
 */
export const toSameTimeDiffTZ = (
    timeA: Date,
    tzA: string,
    tzB: string,
): DateTime => {
    return DateTime.fromJSDate(timeA, { zone: "UTC" })
        .setZone(tzA, { keepLocalTime: true })
        .setZone(tzB)
        .setZone("UTC", { keepLocalTime: true });
};

/**
 * Sets the hours and minutes of a date in UTC
 * @param date Date
 * @param time string in format "HH:mm"
 * @returns Date
 */
export const setHoursAndMinutesToDateInUTC = (date: Date, time: string) => {
    return DateTime.fromJSDate(date, { zone: "UTC" })
        .set({
            hour: parseInt(time?.split(":")[0] ?? "0"),
            minute: parseInt(time?.split(":")[1] ?? "0"),
        })
        .toJSDate();
};

export function formatTimestampToUserTimezone(timestamp: Date | null): string {
    if (!timestamp) return "";
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZoneName: "short",
    };

    return new Date(timestamp).toLocaleString(undefined, options);
}

export const hasEnded = (
    startTime: Date,
    endTime: Date | null,
    timezone: string,
) => {
    const start = DateTime.fromJSDate(startTime, {
        zone: "UTC",
    }).setZone(timezone, { keepLocalTime: true });

    const end =
        endTime &&
        DateTime.fromJSDate(endTime, {
            zone: "UTC",
        }).setZone(timezone, { keepLocalTime: true });

    if (start < DateTime.now() && (end === null || end < DateTime.now()))
        return true;

    return false;
};

type EventWithDate<T> = T & { startTime?: Date };
type SortOrder = "asc" | "desc";

/**
 * Groups events by date and sorts them in the specified order.
 *
 * @param events - Array of events containing a startTime field.
 * @param sortOrder - Sort order, either "asc" for ascending or "desc" for descending. Default is "desc".
 * @returns An array of tuples containing the date as a timestamp and the corresponding events.
 */
export const groupAndSortByDate = <T>(
    events: EventWithDate<T>[],
    sortOrder: SortOrder = "desc",
): [number, EventWithDate<T>[]][] => {
    const grouped = events.reduce((acc, event) => {
        if (!event.startTime) return acc;

        const dateKey = DateTime.fromJSDate(event.startTime, {
            zone: "utc",
        })
            .startOf("day")
            .toMillis();

        if (!acc.has(dateKey)) acc.set(dateKey, []);
        acc.get(dateKey)!.push(event);

        return acc;
    }, new Map<number, EventWithDate<T>[]>());

    return Array.from(grouped.entries()).sort(([dateA], [dateB]) =>
        sortOrder === "asc" ? dateA - dateB : dateB - dateA,
    );
};

export const hasEndedWithDelay = (
    event: {
        startTime: Date;
        endTime: Date | null;
        timezone: string;
    },
    delayMinutes: number = 0,
) => {
    // data we get out of DB is technically inrepreted as UTC
    // and tz component is stored separately
    // move to local time but in target tz
    const startTime = DateTime.fromJSDate(event.startTime, {
        zone: "UTC",
    })
        .plus({ minutes: delayMinutes })
        .toJSDate();
    const endTime =
        event.endTime &&
        DateTime.fromJSDate(event.endTime, {
            zone: "UTC",
        })
            .plus({ minutes: delayMinutes })
            .toJSDate();
    return hasEnded(startTime, endTime, event.timezone);
};

/**
 * Merge a date and 24h time string (from form field) together
 * @param datetime24h
 * @param datetime
 * @returns
 */
export const mergeToTime = (datetime24h: string, datetime: Date) => {
    if (!datetime24h) return datetime;
    const splittedTime = datetime24h.split(":");
    return DateTime.fromJSDate(datetime, { zone: "UTC" })
        .set({
            hour: parseInt(splittedTime[0]!),
            minute: parseInt(splittedTime[1]!),
        })
        .toUTC(0, { keepLocalTime: true })
        .toJSDate();
};

/**
 * Calculates the time difference between the given start time and the current time, and returns a formatted string representing this difference in days or hours.
 *
 * @param startTime
 * @param timezone
 * @returns A string representing the time difference:
 *          - If the difference is 24 hours or more, it returns "X day(s)"
 *          - If the difference is less than 24 hours, it returns "X hour(s)"
 *          Where X is the rounded number of days or hours, respectively.
 *          The function handles singular cases (1 day, 1 hour) correctly.
 * also handles making day and hour plurals correctly
 */
export const getDaysHoursStr = (startTime: Date, timezone: string): string => {
    const start = DateTime.fromJSDate(startTime, {
        zone: "UTC",
    }).setZone(timezone, { keepLocalTime: true });
    const now = DateTime.now();
    const hDiff = start.diff(now).as("hours");
    const roundedHDiff = Math.round(hDiff);

    // plurality handling
    if (roundedHDiff >= 24) {
        const days = Math.round(roundedHDiff / 24);
        return `${days} day${days !== 1 ? "s" : ""}`;
    } else {
        return `${roundedHDiff} hour${roundedHDiff !== 1 ? "s" : ""}`;
    }
};

export const daysWithEventsMap = (events: { startTime: Date }[]) => {
    const daysWithEvents = new Map<string, boolean>();
    events.forEach((event) => {
        const date = DateTime.fromISO(event.startTime.toISOString(), {
            zone: "UTC",
        })
            .toUTC()
            .toJSDate();
        const key = date.toISOString().split("T")[0];
        daysWithEvents.set(key!, true);
    });
    return daysWithEvents;
};

// #region I hate JS timezones
// Seriously, what's wrong with this god forsaken API?

export const getClientTz = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * We need to fix the timezone of the date to the client's timezone
 *
 * keywords to find this function: client timezone, client tz, client time, client now, convert timezone
 * @param time
 * @param timezone
 * @returns The date sent by the user without any JS interference
 */
export const correctDateToClientTz = (time: Date, timezone: string) => {
    return DateTime.fromJSDate(time)
        .setZone(timezone)
        .setZone("utc", { keepLocalTime: true })
        .toJSDate();
};

/**
 * Calculates the time remaining until an event starts.
 *
 * @param startTime - The start time of the event
 * @param timezone - The timezone to calculate the time difference in
 * @returns An object containing:
 *   - hoursUntil: Number of whole hours until the event
 *   - minutesUntil: Number of remaining minutes (after hours) until the event
 *   - formattedTimeUntil: A formatted string (e.g. "2h 30m", "45m", "now")
 */
export const calculateTimeUntilEvent = (
    startTime: Date,
    timezone: string,
): {
    hoursUntil: number;
    minutesUntil: number;
    formattedTimeUntil: string;
} => {
    const now = DateTime.now().setZone(timezone);
    const eventStart = DateTime.fromJSDate(startTime).setZone(timezone);
    const diff = eventStart.diff(now, ["hours", "minutes"]);

    const hoursUntil = Math.floor(diff.hours);
    const minutesUntil = Math.floor(diff.minutes);

    let formattedTimeUntil = "";
    if (hoursUntil > 0) {
        formattedTimeUntil = `${hoursUntil}h`;
        if (minutesUntil > 0) {
            formattedTimeUntil += ` ${minutesUntil}m`;
        }
    } else if (minutesUntil > 0) {
        formattedTimeUntil = `${minutesUntil}m`;
    } else {
        formattedTimeUntil = "now";
    }

    return { hoursUntil, minutesUntil, formattedTimeUntil };
};
